import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { BulkActionsConfig } from 'types/BulkActionTypes';
import { ListMoreMenuConfig } from 'types/ListTypes';
import ListContext from './ListContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEllipsis } from '@fortawesome/free-solid-svg-icons';

/*
 * ListMoreMenu.tsx
 * Secondary button with an ellipsis icon rendered at the far right of the
 * filter bar, opens a dropdown with extra list actions such as exporting
 * everything within the current filters, importing data and toggling the
 * deleted or deactivated items view.
 */

interface ListMoreMenuProps {
    bulkActions?: BulkActionsConfig;
    moreMenu?: ListMoreMenuConfig;
    inactiveItemsActive?: boolean;
    onToggleInactiveItems?: () => void;
    onExportAll?: () => void;
    size?: 'extra-small' | 'small' | 'medium' | 'large';
}

const ListMoreMenu: React.FC<ListMoreMenuProps> = ({
    bulkActions,
    moreMenu,
    inactiveItemsActive = false,
    onToggleInactiveItems,
    onExportAll,
    size = 'small',
}) => {
    const { t } = useTranslation();
    const history = useHistory();
    const hasRightCheck = useAllowedRight;
    const { listMetaData, hasCustomListColumns, openColumnsModal } = useContext(ListContext);
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close the dropdown when clicking outside it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Determine to show the export option based on the config and the export right
    const showExportItem = Boolean(bulkActions?.objectKey) && (bulkActions?.showExport ?? true) && hasRightCheck('can_export');

    // Determine to show the import and inactive items options from the config
    const showImportItem = Boolean(moreMenu?.importObject);
    const inactiveItemsToggle = moreMenu?.inactiveItemsToggle;

    // Determine to show the column selection option based on the feature
    const showColumnsItem = Boolean(hasCustomListColumns && openColumnsModal);

    // Disable the export option when the current filters match no rows
    const exportDisabled = listMetaData.totalRows === 0;

    // Hide the whole button when it would render an empty menu
    if (!showExportItem && !showImportItem && !inactiveItemsToggle && !showColumnsItem) {
        return null;
    }

    // Handle a click on the export option
    const handleExportClick = () => {
        if (exportDisabled) {
            return;
        }
        setIsOpen(false);
        onExportAll?.();
    };

    // Handle a click on the import data option
    const handleImportClick = () => {
        setIsOpen(false);
        history.push(`/import-data/${moreMenu?.importObject}/`);
    };

    // Handle a click on the deleted or deactivated items option
    const handleInactiveItemsClick = () => {
        setIsOpen(false);
        onToggleInactiveItems?.();
    };

    // Handle a click on the choose columns option
    const handleColumnsClick = () => {
        setIsOpen(false);
        openColumnsModal?.();
    };

    return (
        <div className='dropdown-button list-more-menu' ref={menuRef}>
            <button type='button'
                    onClick={() => setIsOpen(!isOpen)}
                    className={`button button-${size} button-secondary list-more-menu-button ${isOpen || inactiveItemsActive ? 'button-active' : ''}`}
                    aria-label={t('lists.more_menu.button_label')}>
                <FontAwesomeIcon icon={faEllipsis} />
            </button>
            {isOpen && (
                <div className='dropdown-button-menu'>
                    {showExportItem && (
                        <div className={`dropdown-button-item ${exportDisabled ? 'disabled' : ''}`}
                             onClick={handleExportClick}>
                            {t('lists.more_menu.export_label')}
                        </div>
                    )}
                    {showImportItem && (
                        <div className='dropdown-button-item' onClick={handleImportClick}>
                            {t('lists.more_menu.import_label')}
                        </div>
                    )}
                    {showColumnsItem && (
                        <div className='dropdown-button-item' onClick={handleColumnsClick}>
                            {t('lists.more_menu.choose_columns_label')}
                        </div>
                    )}
                    {inactiveItemsToggle && (
                        <div className='dropdown-button-item' onClick={handleInactiveItemsClick}>
                            {t(inactiveItemsToggle.semantic === 'deactivated'
                                ? 'lists.more_menu.deactivated_items_label'
                                : 'lists.more_menu.deleted_items_label')}
                            {inactiveItemsActive && (
                                <FontAwesomeIcon
                                    icon={faCheck}
                                    className='filter-dropdown-icon' />
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ListMoreMenu;
